import PlusCircleOutlined from '@ant-design/icons/PlusCircleOutlined'
import { Button } from 'antd'
import { OntologyField, useOntology } from 'features/dynamicOntology'
import findIndex from 'lodash/findIndex'
import isNil from 'lodash/isNil'
import React, {
  Dispatch,
  FC,
  SetStateAction,
  useCallback,
  useMemo,
} from 'react'
import styled from 'styled-components'
import CantileverTheme from 'styles/cantileverTheme'
import { RolodexHistogramFilter, RolodexHistogramFilterOp } from 'types'
import { v4 } from 'uuid'

import {
  DrilldownFilterRow,
  FIELD_TYPE_OPERATORS,
  FilterState,
  UNARY_OPERATORS,
} from './DrilldownFilterRow'

export type DrilldownFilterViewProps = unknown

export type Filter = {
  id: string
  state: FilterState
}

export function toRolodexFilter(
  filterState: FilterState
): RolodexHistogramFilter | undefined {
  const { fieldDef } = filterState
  if (!fieldDef) {
    return undefined
  }
  const { operator, value } = filterState
  const { entryType, keyOrField, key } = fieldDef
  const unaryOp = operator && UNARY_OPERATORS.includes(operator)
  const isValid =
    !!operator &&
    (!isNil(value) || unaryOp) &&
    (!Array.isArray(value) || !!value.length)

  if (!isValid) {
    return undefined
  }

  return {
    type: entryType,
    // ID filters are special - they don't have a key, they have a field.
    [keyOrField]: key,
    op: operator,
    value:
      !value || value === true
        ? undefined
        : Array.isArray(value)
          ? ([...value] as string[] | number[])
          : (value as string | number),
  }
}

export const isFilterActive = (filterState: FilterState) =>
  !!toRolodexFilter(filterState)

const FilterViewContainer = styled.div`
  display: flex;
  width: 100%;
  flex-direction: column;
  padding: 0 1rem 1rem 1rem;
`

const FilterViewRow = styled.div`
  display: flex;
  width: 100%;
  flex-direction: row;
  align-items: center;
  gap: 1rem;
  padding: 0.4rem;
  border-radius: 4px;
  transition: all 0.2s ease;
  justify-content: center;

  &:hover {
    background: ${CantileverTheme.colors.grayscale['600']};
  }
`

const AddFilterButton = styled(Button)`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 40px;
  border: 1px dashed ${CantileverTheme.colors.gray['800']};
  border-radius: 4px;
  transition: all 0.2s ease;
  margin: 0.5rem 0.4rem 0 0.4rem;

  &:hover {
    border-color: ${CantileverTheme.colors.SkyBlue};
    color: ${CantileverTheme.colors.SkyBlue};
  }
`

const EmptyStateContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 2rem;
  text-align: center;
  color: ${CantileverTheme.colors.gray['600']};
`

/**
 * The content of the 'Filter Panel' - this renders a series of "DrilldownFilterRow" components.
 */
export const RawDrilldownFilterView: FC<{
  filters: ReadonlyArray<Filter>
  setFilters: Dispatch<SetStateAction<ReadonlyArray<Filter>>>
  allowFilterOps?: ReadonlyArray<RolodexHistogramFilterOp>
}> = ({ filters, setFilters, allowFilterOps }) => {
  const filterIds = filters.map((filter) => filter.id)

  const { fields } = useOntology()

  const defaultFilterState = useMemo<FilterState>(() => {
    const defaultFilterField = Object.keys(fields)[0] as OntologyField
    const fieldDef = fields[defaultFilterField]
    const DEFAULT_OPERATOR = FIELD_TYPE_OPERATORS[
      fieldDef.type ?? 'text'
    ].filter((op) => !allowFilterOps || allowFilterOps.includes(op))[0]

    return {
      field: defaultFilterField,
      fieldDef,
      operator: DEFAULT_OPERATOR,
    }
  }, [allowFilterOps, fields])
  const createFilter = useCallback(
    (id: string): Filter => ({ id: id, state: defaultFilterState }),
    [defaultFilterState]
  )

  const removeFilter = useCallback(
    (id: string) =>
      setFilters((prev) => prev.filter((filter) => filter.id !== id)),
    [setFilters]
  )

  const setFilterStateFunctions = useMemo(
    () =>
      filters.map((_, index) => {
        return (val: SetStateAction<FilterState>) => {
          setFilters((prev) => {
            const newFilterState =
              typeof val === 'function' ? val(prev[index].state) : val

            return prev.map((filter, idx) =>
              idx === index ? { ...filter, state: newFilterState } : filter
            )
          })
        }
      }),
    [filters, setFilters]
  )

  const createNewFilter = useCallback(
    (id?: string) =>
      setFilters((prev) => {
        const indexOfExisting = id ? findIndex(prev, (f) => f.id === id) : -1
        const newFilterId = id ?? v4()
        const newFilters =
          indexOfExisting >= 0
            ? prev.toSpliced(indexOfExisting, 1, createFilter(newFilterId))
            : [...prev, createFilter(newFilterId)]
        return newFilters
      }),
    [createFilter, setFilters]
  )

  // Each filter gets a UUID, which is really just used as the 'key'
  return (
    <FilterViewContainer>
      {filterIds.length === 0 ? (
        <EmptyStateContainer>
          <p>No filters applied. Click below to add a filter.</p>
        </EmptyStateContainer>
      ) : (
        filterIds.map((id, idx) => {
          return (
            <FilterViewRow key={id}>
              <DrilldownFilterRow
                filter={filters[idx]}
                onRemoveFilter={() => removeFilter(id)}
                setFilterState={setFilterStateFunctions[idx]}
                isActive={isFilterActive(filters[idx].state)}
                allowFilterOps={allowFilterOps}
              />
            </FilterViewRow>
          )
        })
      )}
      <AddFilterButton
        type='text'
        onClick={useCallback(() => createNewFilter(), [createNewFilter])}>
        <PlusCircleOutlined style={{ fontSize: '16px', marginRight: '8px' }} />
        Add Filter
      </AddFilterButton>
    </FilterViewContainer>
  )
}

export const DrilldownFilterView = React.memo(RawDrilldownFilterView)
